import { readDcAuthFromStorage } from "./auth";

export const CP_BASE = "/cp";

let lastPayloadLogAt = 0;

const PRODUCTION_HOSTS = new Set(["devcockpit.ai", "www.devcockpit.ai", "178.128.164.231"]);
const DEVELOPMENT_HOSTS = new Set(["dev.devcockpit.ai", "159.65.26.108", "localhost", "127.0.0.1"]);

function normalizeTenantId(value: unknown): string | null {
  if (value === undefined || value === null) return null;
  const tenant = String(value).trim();
  return tenant ? tenant : null;
}

function getRuntimeHostname(): string | null {
  try {
    const hostname = String(window?.location?.hostname || "").trim().toLowerCase();
    return hostname || null;
  } catch {
    return null;
  }
}

function parseStoredJson(key: string): any | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function getRuntimeTenantId(): string | null {
  try {
    return normalizeTenantId((window as any).__DEVCOCKPIT__?.TENANT_ID);
  } catch {
    return null;
  }
}

function getTokenTenantId(token: string | null | undefined): string | null {
  const payload = decodeJwtPayload(String(token || ""));
  return normalizeTenantId(payload?.tid || payload?.tenantId || payload?.tenant);
}

function clearStoredAuthState() {
  try {
    localStorage.removeItem("dc.auth");
    localStorage.removeItem("auth-storage");
    localStorage.removeItem("dc.persist");
    localStorage.removeItem("selectedTenant");
    localStorage.removeItem("tenant");
  } catch {
    // ignore
  }
}

export function isProductionHost(): boolean {
  const hostname = getRuntimeHostname();
  return Boolean(hostname && PRODUCTION_HOSTS.has(hostname));
}

export function isDevelopmentHost(): boolean {
  const hostname = getRuntimeHostname();
  return Boolean(hostname && DEVELOPMENT_HOSTS.has(hostname));
}

export function getDefaultTenantId(): string | null {
  const runtimeTenant = getRuntimeTenantId();
  if (runtimeTenant) return runtimeTenant;

  if (isProductionHost()) return "prod";
  if (isDevelopmentHost()) return "dev";

  const envTenant = normalizeTenantId(import.meta.env.VITE_TENANT_DEFAULT);
  if (envTenant) return envTenant;

  return null;
}

export function requireTenantId(): string {
  const tenantId = getDefaultTenantId();
  if (!tenantId) throw new Error("Missing tenant context");
  return tenantId;
}

export function sanitizeStoredAuthForHost(): boolean {
  if (!isProductionHost()) return false;

  const hostedTenant = getDefaultTenantId();
  if (!hostedTenant) return false;

  const dcAuth = readDcAuthFromStorage();
  const legacyAuth = dcAuth.token
    ? { token: dcAuth.token, tenant: dcAuth.tenant, tenantId: dcAuth.tenant, app_id: dcAuth.appId, appId: dcAuth.appId }
    : null;
  const persistedAuth = parseStoredJson("auth-storage");
  const token = dcAuth.token || legacyAuth?.token || persistedAuth?.state?.token || null;
  const tokenTenant = getTokenTenantId(token);
  const storedTenant = normalizeTenantId(
    legacyAuth?.tenant ||
    legacyAuth?.tenantId ||
    persistedAuth?.state?.selectedTenant ||
    localStorage.getItem("selectedTenant") ||
    localStorage.getItem("tenant")
  );

  if (tokenTenant && tokenTenant !== hostedTenant) {
    clearStoredAuthState();
    return true;
  }

  if (tokenTenant === hostedTenant) {
    let changed = false;

    if (legacyAuth && storedTenant !== hostedTenant) {
      localStorage.setItem(
        "dc.auth",
        JSON.stringify({
          ...legacyAuth,
          tenant: hostedTenant,
          tenantId: hostedTenant,
        })
      );
      changed = true;
    }

    if (persistedAuth?.state && persistedAuth.state.selectedTenant !== hostedTenant) {
      persistedAuth.state.selectedTenant = hostedTenant;
      localStorage.setItem("auth-storage", JSON.stringify(persistedAuth));
      changed = true;
    }

    if (localStorage.getItem("selectedTenant") !== null || localStorage.getItem("tenant") !== null) {
      localStorage.removeItem("selectedTenant");
      localStorage.removeItem("tenant");
      changed = true;
    }

    return changed;
  }

  if (storedTenant && storedTenant !== hostedTenant) {
    clearStoredAuthState();
    return true;
  }

  return false;
}

export class CpHttpError extends Error {
  status: number;
  code: string | null;
  detail: string | null;

  constructor(status: number, statusText: string, detail: string | null, code: string | null) {
    super(`HTTP ${status}: ${statusText}${detail ? ` (${detail})` : ""}`);
    this.name = "CpHttpError";
    this.status = status;
    this.detail = detail;
    this.code = code;
  }
}

export async function edgeFetch(path: string, init: RequestInit = {}) {
  return fetch(path, { credentials: "omit", ...init });
}

function decodeJwtPayload(token: string): any | null {
  try {
    const parts = String(token || "").split(".");
    if (parts.length < 2) return null;
    const base64Url = parts[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), "=");
    const json = atob(padded);
    return JSON.parse(json);
  } catch {
    return null;
  }
}

function isJwtExpired(token: string): boolean {
  const payload = decodeJwtPayload(token);
  const exp = payload?.exp;
  if (!exp || typeof exp !== "number") return false;
  return Date.now() >= (exp * 1000 - 30_000);
}

/** Build /cp/* without accidental /cp/cp/* - AUTO-ADDS JWT FROM LOCALSTORAGE OR EMBED SESSION */
export async function cpFetch(path: string, init: RequestInit = {}): Promise<any> {
  sanitizeStoredAuthForHost();

  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  const withPrefix = normalizedPath.startsWith(`${CP_BASE}/`)
    ? normalizedPath
    : `${CP_BASE}${normalizedPath}`;
  const collapsed = withPrefix.replace(/^\/cp\/(?:cp\/)+/, "/cp/");

  const debugPayloads = (() => {
    try {
      return localStorage.getItem("dc.debug.payloads") === "1";
    } catch {
      return false;
    }
  })();

  let token = null;
  let tenantId = getDefaultTenantId();
  let appId: string | null = null;

  // Embed iframe: prefer scoped embed token over host session JWT.
  try {
    const { getEmbedSession } = await import("@/features/mapper/embedContext");
    const embedSession = getEmbedSession();
    if (embedSession?.token) {
      token = embedSession.token;
      tenantId = embedSession.tenantId || tenantId;
      appId = embedSession.appId || appId;
    }
  } catch {
    // embed module optional when mapper not loaded
  }

  if (!token) {
    try {
      const dcAuth = readDcAuthFromStorage();
      if (dcAuth.token) {
        token = dcAuth.token;
        tenantId = normalizeTenantId(dcAuth.tenant) || tenantId;
        appId = dcAuth.appId || null;
      }
    } catch (e) {
      console.warn("Failed to read dc.auth from localStorage:", e);
    }
  }

  const headers = new Headers((init as any).headers || {});

  const bodyAny = (init as any).body;
  const isFormData =
    bodyAny &&
    typeof bodyAny === "object" &&
    ((typeof FormData !== "undefined" && bodyAny instanceof FormData) ||
      Object.prototype.toString.call(bodyAny) === "[object FormData]" ||
      (typeof (bodyAny as any).append === "function" && typeof (bodyAny as any).get === "function"));
  if (!isFormData && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  if (token && !isJwtExpired(token)) headers.set("Authorization", `Bearer ${token}`);
  if (tenantId && !headers.has("x-tenant-id")) headers.set("x-tenant-id", tenantId);

  try {
    const lsApp = localStorage.getItem("dc.app_id");
    if (!appId && lsApp) appId = lsApp;
  } catch {
    // ignore
  }
  if (!headers.has("x-app-id") && appId) {
    headers.set("x-app-id", appId);
  }

  const response = await edgeFetch(collapsed, { ...init, headers });

  if (debugPayloads) {
    const cl = response.headers.get("content-length");
    const now = Date.now();
    const n = cl ? Number(cl) : NaN;
    const isLarge = Number.isFinite(n) && n > 500_000;
    const isError = response.status >= 400;
    const shouldLog = isLarge || isError || (now - lastPayloadLogAt) > 5000;
    if (shouldLog) {
      lastPayloadLogAt = now;
      console.debug("[cpFetch]", { url: collapsed, method: init?.method || "GET", status: response.status, contentLength: cl });
    }
  }

  if (!response.ok) {
    let detail: string | null = null;
    let code: string | null = null;
    try {
      const text = await response.text();
      try {
        const json = text ? JSON.parse(text) : null;
        code = json?.error ? String(json.error) : null;
        detail = code || (json?.message && String(json.message)) || null;
      } catch {
        detail = text ? text.slice(0, 200) : null;
      }
    } catch {
      detail = null;
    }
    throw new CpHttpError(response.status, response.statusText, detail, code);
  }

  return response.json();
}

export async function cpFetchJson<T>(path: string, init: RequestInit = {}): Promise<T> {
  return (await cpFetch(path, init)) as T;
}

export function createAuthHeaders(token: string, tenantId: string) {
  return {
    Authorization: `Bearer ${token}`,
    "x-tenant-id": tenantId,
  };
}

export async function edgeFetchWithAuth(
  path: string,
  token?: string,
  tenantId?: string,
  init: RequestInit = {}
) {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(init.headers as Record<string, string>),
  };

  if (token) headers.Authorization = `Bearer ${token}`;
  if (tenantId) headers["x-tenant-id"] = tenantId;

  return fetch(path, { credentials: "omit", ...init, headers });
}
